import React from 'react';

import { Chip, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';
import { ApaReadField, ApaRetentionReadFields } from '../AssetProcessingApaSections/ApaReadField';

export type CentralProcessingActivityPurpose = {
  id: number;
  name: string;
  legalBasis: string;
  legalBasisComment: string;
  liaRequired: boolean;
  retentionTrigger: string;
  retentionValue: number | null;
  retentionUnit: string;
  retentionComment: string;
};

export type CentralProcessingActivity = {
  name: string;
  description: string;
  processingRole: string;
  dataSubjectCategories: string[];
  personalDataCategories: string[];
  recipientCategories: string[];
  minimumSecurityMeasures: string[];
  purposes: CentralProcessingActivityPurpose[];
};

type Props = {
  activity: CentralProcessingActivity;
};

const EMPTY_VALUE = '—';

const ProcessingActivitySidePanel: React.FC<Props> = ({ activity }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.apa_side_panel;
  const processingRoleMessages = processing_activities_messages.processing_role as Record<string, string[]>;

  const processingRoleMessage = activity.processingRole ? processingRoleMessages[activity.processingRole] : undefined;
  const processingRoleLabel = processingRoleMessage ? t(processingRoleMessage) : activity.processingRole || EMPTY_VALUE;

  const renderCategoryChips = (items: string[]) => {
    if (items.length === 0) {
      return EMPTY_VALUE;
    }

    return (
      <div className={styles.categoryTagRow}>
        {items.map(item => (
          <Chip key={item} className={styles.categoryChip} label={item} size='small' />
        ))}
      </div>
    );
  };

  return (
    <aside className={styles.sidePanel}>
      <Typography gutterBottom variant='h3'>
        {t(messages.title)}
      </Typography>
      <Typography color='textSecondary' gutterBottom variant='body2'>
        {t(messages.read_only_note)}
      </Typography>
      <div className={styles.fieldList}>
        <div>
          <div className={styles.fieldLabel}>{t(messages.name)}</div>
          <div>{activity.name}</div>
        </div>
        <div>
          <div className={styles.fieldLabel}>{t(messages.description)}</div>
          <div className={styles.preLineValue}>{activity.description || EMPTY_VALUE}</div>
        </div>
        <div>
          <div className={styles.fieldLabel}>{t(messages.processing_role)}</div>
          <div>{processingRoleLabel}</div>
        </div>
        <div>
          <div className={styles.fieldLabel}>{t(messages.data_subjects)}</div>
          <div>{renderCategoryChips(activity.dataSubjectCategories)}</div>
        </div>
        <div>
          <div className={styles.fieldLabel}>{t(messages.personal_data)}</div>
          <div>{renderCategoryChips(activity.personalDataCategories)}</div>
        </div>
        <div>
          <div className={styles.fieldLabel}>{t(messages.recipients)}</div>
          <div>{renderCategoryChips(activity.recipientCategories)}</div>
        </div>
        <div>
          <div className={styles.fieldLabel}>{t(messages.minimum_security)}</div>
          <div className={styles.preLineValue}>
            {activity.minimumSecurityMeasures.length > 0 ? activity.minimumSecurityMeasures.join(', ') : EMPTY_VALUE}
          </div>
        </div>
      </div>
      {activity.purposes.map(purpose => (
        <div key={purpose.id || purpose.name} className={styles.fieldList} style={{ marginTop: 16 }}>
          <Typography gutterBottom variant='h4'>
            {purpose.name}
          </Typography>
          <ApaReadField label={t(messages.legal_basis)} preserveLineBreaks={false} value={purpose.legalBasis} />
          <ApaReadField label={t(messages.legal_basis_comment)} value={purpose.legalBasisComment} />
          <ApaReadField
            label={t(messages.lia_required)}
            preserveLineBreaks={false}
            value={purpose.liaRequired ? t(messages.yes) : t(messages.no)}
          />
          <ApaRetentionReadFields
            commentLabel={t(messages.retention_comment)}
            periodAndUnitLabel={t(messages.retention_period_and_unit)}
            retentionComment={purpose.retentionComment}
            retentionTrigger={purpose.retentionTrigger}
            retentionUnit={purpose.retentionUnit}
            retentionValue={purpose.retentionValue}
            triggerLabel={t(messages.retention_trigger)}
          />
        </div>
      ))}
    </aside>
  );
};

export default ProcessingActivitySidePanel;
